import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { Brain, Sun, CloudRain, Package, CalendarRange, ArrowRight, Settings2, Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const RECOMMEND_ANOTHER_REASONS = [
  { key: "customer_interested", label: "Customer currently interested" },
  { key: "deposit_sale_pending", label: "Deposit / sale pending" },
  { key: "temporarily_unavailable", label: "Temporarily unavailable" },
  { key: "do_not_market_now", label: "Do not market this boat right now" },
  { key: "management_priority", label: "Management priority changed" },
  { key: "show_another", label: "Just show me another option" },
  { key: "other", label: "Other" },
];

const PHASE_TONE = {
  "Ramp-up": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Peak season": "bg-gold/15 text-gold border-gold/40",
  "Late season": "bg-amber-50 text-amber-700 border-amber-200",
  "Preseason": "bg-blue-50 text-blue-700 border-blue-200",
  "Off-season": "bg-slate-100 text-slate-600 border-slate-200",
};

const FISHING_CATS = ["Center Console", "Bay Boat", "Skiff", "Bass Boat", "Sport Fishing"];
const FAMILY_CATS = ["Pontoon", "Deck Boat", "Bowrider", "Jet Boat", "Cuddy Cabin"];
const deriveObjective = (cat) => FISHING_CATS.includes(cat) ? "fishing"
  : FAMILY_CATS.includes(cat) ? "family" : cat === "Wake & Ski" ? "performance" : "general";
const deriveTone = (phaseLabel, cat) => ["Yacht", "Cruiser"].includes(cat) ? "High-end Luxury"
  : phaseLabel === "Late season" ? "Urgent Sale" : FAMILY_CATS.includes(cat) ? "Family Warmth" : "High Energy";
const strategyLabel = (phaseLabel, cat) => {
  const base = FISHING_CATS.includes(cat) ? "fishing" : "boating";
  const ph = (phaseLabel || "").toLowerCase();
  if (ph.includes("late")) return `Late-season ${base} urgency`;
  if (ph.includes("peak")) return `Peak-season ${base} demand`;
  if (ph.includes("ramp")) return `Early-season ${base} momentum`;
  if (ph.includes("pre")) return `Preseason ${base} awareness`;
  return `${phaseLabel || "Seasonal"} ${base} strategy`;
};

export const RecJetMindCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [building, setBuilding] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [showReasons, setShowReasons] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [note, setNote] = useState("");
  const [setAside, setSetAside] = useState(null);
  const [recommending, setRecommending] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const recommendAnother = async (reason, reasonNote) => {
    const src = (fin && fin.available) ? fin : data;
    const rb = src?.recommended_boat;
    if (!rb?.id) return;
    const prevName = [rb.year, rb.manufacturer, rb.model].filter(Boolean).join(" ") || "this unit";
    setRecommending(true);
    try {
      const { data: res } = await api.post("/intelligence/recommendation/another",
        { inventory_id: rb.id, reason, note: reasonNote || null });
      setFin(res?.final || null);
      setSetAside({ inventory_id: rb.id, label: res?.set_aside?.label, name: prevName });
      setShowReasons(false); setOtherOpen(false); setNote("");
      toast.success("RecJet Mind found a different recommendation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not find another recommendation");
    } finally {
      setRecommending(false);
    }
  };

  const undoSetAside = async () => {
    if (!setAside) return;
    setRecommending(true);
    try {
      const { data: res } = await api.post("/intelligence/recommendation/suppression/clear",
        { inventory_id: setAside.inventory_id });
      setFin(res?.final || null);
      setSetAside(null);
      toast.success("Restored. RecJet Mind can recommend this boat again");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not undo");
    } finally {
      setRecommending(false);
    }
  };

  const answerQuestion = async (answer) => {
    const q = (fin && fin.dealer_question) || null;
    if (!q) return;
    setAnswering(true);
    try {
      const { data: res } = await api.post("/intelligence/decision-resolution/answer",
        { signature: q.signature, dimension: q.dimension, question: q.question, answer });
      setFin(res?.final || null);
      toast.success("Thanks, RecJet Mind updated its recommendation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply your answer");
    } finally {
      setAnswering(false);
    }
  };

  const expressBuild = async () => {
    const src = (fin && fin.available) ? fin : data;
    const rb = src.recommended_boat;
    const geo = src.geo_targeting || null;
    const isOrganic = src.organic_paid_strategy === "organic" || !src.dealer_spend;
    const recjet = {
      category: src.category, phase_label: src.phase_label || data?.phase_label, recommendation: src.recommendation,
      creative_direction: src.creative_direction, suggested_message: src.suggested_message,
      reasons: src.reasons, recommended_boat: rb, strategy_label: strategyLabel(src.phase_label || data?.phase_label, src.category),
      recommended_budget: isOrganic ? null : src.dealer_spend,
      budget_type: isOrganic ? null : "total_campaign",
      spend_explanation: src.spend_explanation, budget_context: src.budget_context || null,
      organic_paid_strategy: src.organic_paid_strategy, paid_timing: src.paid_timing,
      geo_targeting: geo,
    };
    if (!rb?.id) {
      navigate(`/create-campaign?category=${encodeURIComponent(src.category)}&fromRecjet=1`, { state: { recjet } });
      return;
    }
    const objective = src.recommended_objective || deriveObjective(src.category);
    const tone = deriveTone(src.phase_label || data?.phase_label, src.category);
    setBuilding(true);
    try {
      const { data: gen } = await api.post("/campaigns/generate",
        { boat_id: rb.id, objective, tone, selected_photos: [], recjet, campaign_mode: "recjet_mind" });
      const recjetDisplay = { ...recjet, best_fit_buyer: gen.authoritative?.best_fit_buyer || recjet.best_fit_buyer };
      const config = { boat_id: rb.id, objective: gen.authoritative?.objective || objective, tone,
                       selected_photos: gen.boat?.photos || [], recjet: recjetDisplay,
                       coop: { oem_support_available: false, oem_support_elected: false },
                       strategy_source: gen.campaign_mode || "recjet_mind", strategy_snapshot: gen.strategy_snapshot || null,
                       budget: recjet.recommended_budget ?? null,
                       targeting: geo ? { markets: geo.markets || [], radius_miles: geo.recommended_radius_miles ?? null, buyer_geo_note: geo.buyer_geo_note || null } : {},
                       territory: geo?.primary_market || null };
      navigate("/campaign-results", { state: { results: gen.results, boat: gen.boat, config, recjet: recjetDisplay } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build campaign");
      setBuilding(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/intelligence/market").then(({ data }) => setData(data)).catch(() => setData(null)),
      api.get("/intelligence/decision-resolution").then(({ data }) => setFin(data?.final || null)).catch(() => setFin(null)),
    ]).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  const rec = (fin && fin.available) ? fin : data;
  const CONF_TONE = {
    "Strong Recommendation": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Recommendation With Competing Factors": "bg-amber-50 text-amber-700 border-amber-200",
    "Dealer Input Recommended": "bg-gold/15 text-gold border-gold/40",
  };
  const WeatherIcon = data?.weekend?.fair ? Sun : CloudRain;

  const empty = !data || data.available === false;
  const rb = rec?.recommended_boat;
  const rbPrice = rb?.price ?? rb?.price_context?.price ?? null;
  const priceText = rbPrice != null && rbPrice !== "" ? `$${Number(rbPrice).toLocaleString()}` : null;
  // Top verified decision factors for the compact view (2 to 3), reusing existing evidence.
  const topFactors = (((rec?.why_this_boat || []).concat(rec?.why_now || [])).length
    ? (rec.why_this_boat || []).concat(rec.why_now || [])
    : ((rec?.why_labels && rec.why_labels.length ? rec.why_labels : rec?.reasons) || [])).slice(0, 3);

  return (
    <section data-testid="recjet-mind-card">
      <div className="relative overflow-hidden rounded-3xl navy-gradient p-5 sm:p-6 text-white">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center"><Brain className="w-4 h-4 text-navy" /></div>
            <div>
              <h2 className="font-serif text-lg font-bold">RecJet Mind Command Center</h2>
              <p className="text-xs text-slate-300">What to market, when and why.</p>
            </div>
          </div>
          <button onClick={() => navigate("/settings")} className="text-slate-300 hover:text-white transition-colors" data-testid="recjet-mind-configure" title="Configure in Settings">
            <Settings2 className="w-4 h-4" />
          </button>
        </div>

        {empty ? (
          <div className="text-slate-300 text-sm" data-testid="recjet-mind-empty">
            {data?.message || "RecJet Mind will show a recommendation once your inventory is set up."}
          </div>
        ) : (
          <>
            {/* COMPACT SUMMARY */}
            <div className="flex flex-col lg:flex-row gap-5">
              {/* LEFT: boat photo, identity, inventory info, actions */}
              <div className="lg:w-[320px] shrink-0 flex flex-col" data-testid="recjet-recommended-boat">
                <div className="w-full aspect-[16/10] rounded-xl overflow-hidden bg-white/10">
                  {rb?.thumbnail
                    ? <img src={resolveImg(rb.thumbnail)} alt="" className="w-full h-full object-cover" data-testid="recjet-recommended-thumb" />
                    : <div className="w-full h-full flex items-center justify-center"><Package className="w-7 h-7 text-slate-400" /></div>}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="font-serif text-lg font-bold" data-testid="recjet-mind-category">{rec.category || data.category}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PHASE_TONE[rec.phase_label || data.phase_label] || "bg-white/10 text-white border-white/20"}`} data-testid="recjet-mind-phase">{rec.phase_label || data.phase_label}</span>
                  {data.override?.active && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-gold/15 text-gold border-gold/40" data-testid="recjet-mind-override" title={`Normal phase: ${data.override.normal_phase_label}`}>Override active</span>
                  )}
                </div>

                {rb && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-gold font-bold mt-1.5">Recommended inventory</div>
                    <div className="font-semibold text-sm" data-testid="recjet-recommended-name">{[rb.year, rb.manufacturer, rb.model].filter(Boolean).join(" ")}</div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-300 mt-1" data-testid="recjet-recommended-info">
                      {priceText && <span className="font-semibold text-white">{priceText}</span>}
                      {priceText && (rb.stock_number || rb.days_in_inventory != null) && <span className="text-slate-500">&bull;</span>}
                      {rb.stock_number && <span>Stock #{rb.stock_number}</span>}
                      {rb.stock_number && rb.days_in_inventory != null && <span className="text-slate-500">&bull;</span>}
                      {rb.days_in_inventory != null && <span>{rb.days_in_inventory} days in inventory</span>}
                    </div>
                  </>
                )}

                <div className="mt-4 flex flex-col gap-2">
                  <button onClick={expressBuild} disabled={building}
                    className="inline-flex items-center justify-center gap-2 gold-gradient text-navy font-bold text-sm px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60"
                    data-testid="recjet-mind-build-campaign">
                    {building ? <><Loader2 className="w-4 h-4 animate-spin" /> Building...</> : <>Build Recommended Campaign <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  {rb?.id && (
                    <button onClick={() => setShowReasons((v) => !v)} disabled={recommending}
                      className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white border border-white/30 px-4 py-2.5 rounded-full hover:bg-white/10 transition-colors disabled:opacity-60"
                      data-testid="recjet-mind-recommend-another">
                      {recommending ? <><Loader2 className="w-4 h-4 animate-spin" /> Finding another...</> : <><RefreshCw className="w-4 h-4" /> Recommend Another</>}
                    </button>
                  )}
                </div>

                {showReasons && (
                  <div className="mt-3 bg-white/10 border border-white/15 rounded-xl p-3" data-testid="recjet-mind-reasons-picker">
                    <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-2">Why would you like another recommendation?</div>
                    <div className="flex flex-wrap gap-2">
                      {RECOMMEND_ANOTHER_REASONS.map((r) => (
                        <button key={r.key} type="button" disabled={recommending}
                          onClick={() => r.key === "other" ? setOtherOpen(true) : recommendAnother(r.key)}
                          data-testid={`recjet-mind-reason-${r.key}`}
                          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/25 hover:bg-white/20 transition-colors disabled:opacity-60">{r.label}</button>
                      ))}
                    </div>
                    {otherOpen && (
                      <div className="mt-3 flex items-center gap-2">
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a short note (optional)"
                          data-testid="recjet-mind-reason-note"
                          className="flex-1 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-gold" />
                        <button type="button" disabled={recommending} onClick={() => recommendAnother("other", note)}
                          data-testid="recjet-mind-reason-note-submit"
                          className="px-3 py-1.5 rounded-full text-xs font-bold gold-gradient text-navy disabled:opacity-60 shrink-0">Submit</button>
                      </div>
                    )}
                  </div>
                )}
                {setAside && (
                  <div className="mt-3 flex items-center justify-between gap-2 bg-white/10 border border-white/15 rounded-xl px-3 py-2" data-testid="recjet-mind-setaside-banner">
                    <span className="text-xs text-slate-200">Set aside: <span className="font-semibold text-white">{setAside.name}</span>{setAside.label ? ` (${setAside.label})` : ""}</span>
                    <button type="button" onClick={undoSetAside} disabled={recommending} data-testid="recjet-mind-undo-setaside" className="text-xs font-bold text-gold hover:underline disabled:opacity-60 shrink-0">Undo</button>
                  </div>
                )}
              </div>

              {/* RIGHT: recommendation, decision factors, question, analysis toggle */}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-[11px] uppercase tracking-widest text-gold font-bold">Recommendation</div>
                  {rec.confidence_state && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${CONF_TONE[rec.confidence_state] || "bg-white/10 text-white border-white/20"}`} data-testid="recjet-mind-confidence">{rec.confidence_state}</span>
                  )}
                </div>
                <p className="text-base font-semibold leading-snug" data-testid="recjet-mind-recommendation">{rec.recommendation}</p>
                {rec.signals_evaluated > 0 && (
                  <div className="text-[11px] text-slate-400 mt-1" data-testid="recjet-mind-signal-count">RecJet Mind evaluated {rec.signals_evaluated} current signals for this recommendation</div>
                )}
                {topFactors.length > 0 && (
                  <ul className="mt-2.5 space-y-1" data-testid="recjet-mind-top-factors">
                    {topFactors.map((r, i) => (
                      <li key={i} className="text-sm text-slate-200 flex items-start gap-2"><span className="text-gold mt-1">&bull;</span><span>{r}</span></li>
                    ))}
                  </ul>
                )}
                {rec.dealer_input_recommended && rec.dealer_question && (
                  <div className="mt-3 bg-gold/10 border border-gold/30 rounded-xl p-3" data-testid="recjet-mind-dealer-question">
                    <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-1">Your input helps</div>
                    <p className="text-sm text-white mb-3">{rec.dealer_question.question}</p>
                    <div className="flex flex-wrap gap-2">
                      {(rec.dealer_question.options || []).map((opt) => (
                        <button key={opt} type="button" onClick={() => answerQuestion(opt)} disabled={answering}
                          data-testid={`recjet-mind-answer-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/25 hover:bg-white/20 transition-colors disabled:opacity-60">{opt}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-auto pt-4">
                  <button onClick={() => setShowFull((v) => !v)} data-testid="recjet-mind-toggle-analysis"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:text-white transition-colors">
                    {showFull ? <>Hide Full Decision Analysis <ChevronUp className="w-4 h-4" /></> : <>View Full Decision Analysis <ChevronDown className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            </div>

            {/* FULL DECISION ANALYSIS (progressive disclosure) */}
            {showFull && (
              <div className="mt-5 pt-5 border-t border-white/10 grid lg:grid-cols-3 gap-6" data-testid="recjet-mind-full-analysis">
                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                    {rec.rationale && <p className="text-sm text-slate-200" data-testid="recjet-mind-rationale">{rec.rationale}</p>}
                    {rec.confidence_state === "Recommendation With Competing Factors" && rec.competing_factor && (
                      <div className="text-sm text-amber-200 mt-2 flex items-start gap-2" data-testid="recjet-mind-competing-factor"><span className="font-semibold shrink-0">Competing factor:</span><span>{rec.competing_factor}</span></div>
                    )}
                    <div className="text-sm text-slate-300 mt-2" data-testid="recjet-mind-creative">Creative direction: {rec.creative_direction}</div>
                    {rec.suggested_message && <div className="text-sm text-white mt-2 italic border-l-2 border-gold pl-3" data-testid="recjet-mind-message">Suggested message: &ldquo;{rec.suggested_message}&rdquo;</div>}
                    {rec.event_timing_note && <div className="text-sm text-gold mt-2 flex items-center gap-2" data-testid="recjet-mind-event-timing"><CalendarRange className="w-3.5 h-3.5 shrink-0" /><span>{rec.event_timing_note}</span></div>}
                  </div>
                  <div data-testid="recjet-mind-decision-snapshot" className="space-y-3">
                    {(rec.why_this_boat?.length || rec.why_now?.length) ? (
                      <>
                        {rec.why_this_boat?.length > 0 && (
                          <div data-testid="recjet-mind-why-boat">
                            <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-1.5">Why this boat</div>
                            <ul className="space-y-1">{rec.why_this_boat.map((r, i) => <li key={i} className="text-sm text-slate-200 flex items-start gap-2"><span className="text-gold mt-1">&bull;</span><span>{r}</span></li>)}</ul>
                          </div>
                        )}
                        {rec.why_now?.length > 0 && (
                          <div data-testid="recjet-mind-why-now">
                            <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-1.5">Why now</div>
                            <ul className="space-y-1">{rec.why_now.map((r, i) => <li key={i} className="text-sm text-slate-200 flex items-start gap-2"><span className="text-gold mt-1">&bull;</span><span>{r}</span></li>)}</ul>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">Why RecJet Mind recommends this</div>
                        <ul className="space-y-1" data-testid="recjet-mind-reasons">{((rec.why_labels && rec.why_labels.length ? rec.why_labels : rec.reasons) || []).map((r, i) => <li key={i} className="text-sm text-slate-200 flex items-start gap-2"><span className="text-gold mt-1">&bull;</span><span>{r}</span></li>)}</ul>
                      </div>
                    )}
                    {rec.recommended_approach && (
                      <div data-testid="recjet-mind-approach">
                        <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-1">Recommended approach</div>
                        <div className="text-sm font-semibold text-white">{rec.recommended_approach}</div>
                        {rec.spend_explanation && <div className="text-xs text-slate-300 mt-1.5" data-testid="recjet-mind-spend-explanation">{rec.spend_explanation}</div>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="bg-white/10 rounded-xl p-4 border border-white/10" data-testid="recjet-mind-decision-context">
                    <div className="text-[11px] uppercase tracking-widest text-gold font-bold mb-3">Decision context</div>
                    {(rec.decision_context && rec.decision_context.length > 0) ? (
                      <dl className="space-y-2.5">
                        {(data.weekend?.days?.length ? rec.decision_context.filter((c) => c.label !== "Weekend weather") : rec.decision_context).map((c, i) => (
                          <div key={i} className="flex items-start justify-between gap-3" data-testid={`recjet-mind-context-${i}`}>
                            <dt className="text-xs text-slate-300 shrink-0">{c.label}</dt>
                            <dd className="text-sm font-semibold text-white text-right">{c.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <div className="text-sm text-slate-300" data-testid="recjet-mind-context-empty">Add inventory, floorplan or buyer data in Settings to surface more decision context.</div>
                    )}
                  </div>
                  {data.weekend?.days?.length > 0 && (
                    <div className="bg-white/10 rounded-xl p-3 border border-white/10">
                      <div className="flex items-center gap-2 mb-1.5"><WeatherIcon className="w-4 h-4 text-gold shrink-0" /><div className="text-xs text-slate-300">Weekend forecast</div></div>
                      <div className="space-y-1" data-testid="recjet-mind-weather">
                        {data.weekend.days.map((d) => (
                          <div key={d.date} className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-slate-200 w-9">{d.day.slice(0, 3)}</span>
                            <span className="font-semibold flex-1">{d.temp_high != null ? `${d.temp_high}F` : "-"}, {d.condition}</span>
                            {d.favorable && <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Good</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!data.weather_enabled && (
                    <button onClick={() => navigate("/settings")} className="w-full text-xs text-gold hover:underline flex items-center justify-center gap-1" data-testid="recjet-mind-enable-weather">
                      Enable weather intelligence <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
